import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { listEnquiries, type AdminEnquiryRow } from "@/lib/admin-enquiries.functions";

export const Route = createFileRoute("/admin/enquiries")({
  head: () => ({
    meta: [
      { title: "Enquiries" },
      { name: "robots", content: "noindex, nofollow" },
      { name: "description", content: "Internal enquiry list." },
    ],
  }),
  validateSearch: (search: Record<string, unknown>) => ({
    key: typeof search["key"] === "string" ? (search["key"] as string) : "",
  }),
  component: AdminEnquiriesPage,
});

function formatSydney(iso: string) {
  try {
    return new Intl.DateTimeFormat("en-AU", {
      timeZone: "Australia/Sydney",
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function AdminEnquiriesPage() {
  const { key } = Route.useSearch();
  const [rows, setRows] = useState<AdminEnquiryRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    if (!key) {
      setError("Access key required.");
      return;
    }
    listEnquiries({ data: { key } })
      .then((result) => {
        if (active) setRows(result.rows);
      })
      .catch(() => {
        if (active) setError("Access denied.");
      });
    return () => {
      active = false;
    };
  }, [key]);

  return (
    <section className="bg-surface px-6 pb-20 pt-32">
      <div className="mx-auto max-w-7xl">
        <h1 className="font-serif text-3xl text-foreground">Enquiries</h1>
        {error && <p className="mt-6 text-sm text-charcoal">{error}</p>}
        {!error && rows === null && (
          <p className="mt-6 text-sm text-charcoal">Loading…</p>
        )}
        {rows !== null && rows.length === 0 && (
          <p className="mt-6 text-sm text-charcoal">No enquiries yet.</p>
        )}
        {rows !== null && rows.length > 0 && (
          <div className="mt-8 overflow-x-auto rounded-2xl border border-border bg-card">
            <table className="w-full min-w-[900px] text-left text-sm">
              <thead className="border-b border-border text-xs uppercase tracking-wide text-charcoal">
                <tr>
                  <th className="p-3 font-semibold">Name</th>
                  <th className="p-3 font-semibold">Phone</th>
                  <th className="p-3 font-semibold">Company</th>
                  <th className="p-3 font-semibold">Email</th>
                  <th className="p-3 font-semibold">Revenue</th>
                  <th className="p-3 font-semibold">Best time</th>
                  <th className="p-3 font-semibold">Created</th>
                  <th className="p-3 font-semibold">Message</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className="border-b border-border/60 align-top">
                    <td className="p-3 text-foreground">{row.name}</td>
                    <td className="whitespace-nowrap p-3 text-charcoal">{row.phone ?? "—"}</td>
                    <td className="p-3 text-charcoal">{row.company ?? "—"}</td>
                    <td className="p-3 text-charcoal">{row.email}</td>
                    <td className="p-3 text-charcoal">{row.revenue ?? "—"}</td>
                    <td className="p-3 text-charcoal">{row.best_time ?? "—"}</td>
                    <td className="whitespace-nowrap p-3 text-charcoal">
                      {formatSydney(row.created_at)}
                    </td>
                    <td className="p-3 whitespace-pre-wrap text-charcoal">
                      {row.message}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
